/**
 * Data shaping for the Render-style memory chart (MemoryChart.js). Pure functions, so
 * they're tested without rendering a chart.
 *
 * The backend sends `server.memory`: one line per server process (a restart or deploy
 * starts a new line, as on Render), the groups for the Instances filter, and restart
 * markers. See api/telemetry/snapshot.py:build_memory_chart.
 */

const MIN = 60;
const HOUR = 3600;
const DAY = 86400;

// Render's range menu. Samples are kept for 14 days, so 30 days is listed but off.
export const RANGE_OPTIONS = [
  { label: "Last 30 min", sec: 30 * MIN },
  { label: "Last hour", sec: HOUR },
  { label: "Last 4 hours", sec: 4 * HOUR },
  { label: "Last 12 hours", sec: 12 * HOUR },
  { label: "Last 24 hours", sec: DAY },
  { label: "Last 2 days", sec: 2 * DAY },
  { label: "Last 7 days", sec: 7 * DAY },
  { label: "Last 14 days", sec: 14 * DAY },
  { label: "Last 30 days", sec: 30 * DAY, disabled: true, hint: "Samples are kept for 14 days" },
];
export const MIN_WINDOW_SEC = 5 * MIN;
export const MAX_WINDOW_SEC = 14 * DAY;

// One colour per line (Render gives each instance its own). Picked from the line's key, so a
// line keeps its colour when older lines leave the window.
export const LINE_COLORS = [
  "#2dd4bf", "#a3e635", "#60a5fa", "#f472b6", "#fbbf24",
  "#c084fc", "#fb7185", "#34d399", "#38bdf8", "#facc15",
];

function hashKey(key) {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Each line's colour, by run (one server process): from the run's key, but never the same as
 * the run before it on the same server, so a restart always shows as a change of colour. The
 * pieces of one run (a gap breaks its line) keep one colour. */
export function lineColors(lines) {
  const out = new Map();
  const byRun = new Map();
  const lastOfGroup = new Map();
  for (const line of lines) {
    const run = String(line.run ?? line.key);
    if (!byRun.has(run)) {
      let index = hashKey(run) % LINE_COLORS.length;
      if (lastOfGroup.get(line.group) === index) index = (index + 1) % LINE_COLORS.length;
      lastOfGroup.set(line.group, index);
      byRun.set(run, LINE_COLORS[index]);
    }
    out.set(line.key, byRun.get(run));
  }
  return out;
}

/**
 * The runs in the chart (one server process each: a restart or deploy starts a new one), newest
 * first, for the Instances menu the way Render lists instances: when each started, its colour,
 * its time span, and whether it's still running (`current`: its last sample is at the end of a
 * range that ends now). When several servers share the chart, each label names its server.
 */
export function listRuns(memory, endMs, now = Date.now()) {
  const colors = lineColors(memory?.lines || []);
  const groups = new Map((memory?.groups || []).map((g) => [g.key, g]));
  const byRun = new Map();
  for (const line of memory?.lines || []) {
    const run = String(line.run ?? line.key);
    const first = Date.parse(line.first_at);
    const last = Date.parse(line.last_at);
    const item = byRun.get(run) || {
      run, group: line.group, started: line.started || null, limit_mb: line.limit_mb, color: colors.get(line.key),
      first, last,
    };
    item.first = Math.min(item.first, first);
    item.last = Math.max(item.last, last);
    byRun.set(run, item);
  }
  const bucketMs = (memory?.bucket_s || 60) * 1000;
  const live = Math.abs(now - endMs) <= 5 * 60 * 1000;
  return [...byRun.values()].sort((a, b) => b.first - a.first).map((item) => {
    const when = item.started ? `started ${formatMoment(Date.parse(item.started))}` : `from ${formatMoment(item.first)}`;
    const server = groups.get(item.group)?.label || item.group;
    const label = groups.size > 1 ? `${server}, ${when}` : `${when[0].toUpperCase()}${when.slice(1)}`;
    return { ...item, label, current: live && endMs - item.last <= 3 * bucketMs };
  });
}

export function rangeLabel(sec) {
  const preset = RANGE_OPTIONS.find((o) => o.sec === sec && !o.disabled);
  if (preset) return preset.label;
  if (sec % DAY === 0) return `Last ${sec / DAY} days`;
  if (sec % HOUR === 0) return `Last ${sec / HOUR} hours`;
  return `Last ${Math.round(sec / MIN)} min`;
}

export function clampWindow(sec) {
  return Math.max(MIN_WINDOW_SEC, Math.min(MAX_WINDOW_SEC, Math.round(sec)));
}

export function formatMbShort(mb) {
  if (mb == null || Number.isNaN(Number(mb))) return "—";
  const n = Number(mb);
  if (n >= 1024) return `${(n / 1024).toFixed(n >= 10240 ? 0 : 1).replace(/\.0$/, "")} GB`;
  return `${Math.round(n)} MB`;
}

/**
 * Lines to draw and the rows recharts needs, for every run or just one (`run`).
 * mode "percent": memory as a share of each line's own limit (lines with no limit, i.e.
 * computers, can't be drawn and are counted in `hidden`); "total": MB.
 */
export function buildChart(memory, { mode = "percent", run = "all" } = {}) {
  const colors = lineColors(memory?.lines || []);
  const allLines = (memory?.lines || []).map((line) => ({ ...line, color: colors.get(line.key) }));
  const inRun = allLines.filter((line) => run === "all" || String(line.run ?? line.key) === run);
  const lines = mode === "percent" ? inRun.filter((line) => line.limit_mb) : inRun;
  const hidden = inRun.length - lines.length;
  const byTime = new Map();
  let peak = 0;
  for (const line of lines) {
    for (const [iso, mb] of line.points || []) {
      if (mb == null) continue;
      const t = Date.parse(iso);
      if (Number.isNaN(t)) continue;
      const value = mode === "percent" ? (mb / line.limit_mb) * 100 : mb;
      peak = Math.max(peak, value);
      const row = byTime.get(t) || { t };
      row[line.key] = value;
      row[`${line.key}__mb`] = mb;
      byTime.set(t, row);
    }
  }
  const rows = [...byTime.values()].sort((a, b) => a.t - b.t);
  const limits = [...new Set(lines.map((line) => line.limit_mb).filter(Boolean))].sort((a, b) => a - b);
  return { rows, lines, hidden, peak, limits };
}

function stepsUpTo(top, step) {
  const ticks = [];
  for (let i = 0; i * step <= top + 1e-6; i += 1) ticks.push(Math.round(i * step * 100) / 100);
  return ticks;
}

// 1, 2, 2.5 or 5 times a power of ten, at least `raw`.
function roundStep(raw) {
  const power = 10 ** Math.floor(Math.log10(raw));
  return [1, 2, 2.5, 5, 10].map((m) => m * power).find((step) => step >= raw);
}

/**
 * Y axis, the same in both views: 0 to 100% of the limit in quarters, or under Total the same
 * quarters in memory amounts (0 to the limit). Past the limit, or with no limit at all (a
 * computer), round steps of MB or GB instead.
 */
export function yAxis(mode, peak, limits) {
  if (mode === "percent") {
    const top = Math.max(100, Math.ceil(peak / 25) * 25);
    return { domain: [0, top], ticks: stepsUpTo(top, 25) };
  }
  const limit = limits.length ? Math.max(...limits) : 0;
  if (limit && peak <= limit) return { domain: [0, limit], ticks: stepsUpTo(limit, limit / 4) };
  const raw = Math.max(peak, 1) / 4;
  const step = raw >= 256 ? roundStep(raw / 1024) * 1024 : roundStep(raw);   // GB steps once they're big
  const top = Math.max(step, Math.ceil(peak / step) * step);
  return { domain: [0, top], ticks: stepsUpTo(top, step) };
}

/**
 * Event markers, clustered like Render's numbered badges: events closer together than
 * `minGapFraction` of the visible span share one marker that counts them.
 */
export function clusterEvents(events, domain, minGapFraction = 0.02) {
  const [from, to] = domain;
  const gap = Math.max(1, (to - from) * minGapFraction);
  const sorted = (events || [])
    .map((e) => ({ ...e, ms: Date.parse(e.t) }))
    .filter((e) => !Number.isNaN(e.ms) && e.ms >= from && e.ms <= to)
    .sort((a, b) => a.ms - b.ms);
  const clusters = [];
  for (const event of sorted) {
    const last = clusters[clusters.length - 1];
    if (last && event.ms - last.ms <= gap) {
      last.events.push(event);
    } else {
      clusters.push({ ms: event.ms, events: [event] });
    }
  }
  return clusters.map((c) => ({ ...c, count: c.events.length, key: `${c.ms}-${c.events.length}` }));
}

/** Evenly spaced x ticks across the window. */
export function timeTicks(domain, count = 7) {
  const [from, to] = domain;
  if (!(to > from)) return [];
  const step = (to - from) / (count - 1);
  return Array.from({ length: count }, (_, i) => Math.round(from + i * step));
}

function clock(d, withMinutes = true) {
  let h = d.getHours();
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  const m = d.getMinutes();
  return withMinutes || m !== 0 ? `${h}:${String(m).padStart(2, "0")}${ampm}` : `${h}${ampm}`;
}

/**
 * Render's tick labels: "6:48am", and "9/27, 2pm" where the day changes from the tick
 * before. Shown in the viewer's own time zone.
 */
export function formatTick(ms, previousMs) {
  const d = new Date(ms);
  if (previousMs != null && new Date(previousMs).toDateString() !== d.toDateString()) {
    return `${d.getMonth() + 1}/${d.getDate()}, ${clock(d, false)}`;
  }
  return clock(d);
}

/** "Oct 2, 3:15pm" for tooltips and the custom range label. */
export function formatMoment(ms) {
  const d = new Date(ms);
  return `${d.toLocaleString([], { month: "short" })} ${d.getDate()}, ${clock(d)}`;
}

/** What happened, in words that follow the server's name ("notesai-dev restarted"). */
function eventWords(e) {
  if (e.kind === "jump") return `jumped by ${Math.round(e.delta_mb)} MB${e.near_limit ? ", close to its limit" : ""}`;
  if (e.kind === "start") return "started";
  if (e.deploy) return "was replaced by a new run (a deploy)";
  if (e.likelyOom) return "restarted, most likely out of memory";
  return "restarted";
}

/**
 * Markers for the chart: restarts and memory jumps (from the detail lists below the chart)
 * plus server starts and older restarts from the full-range series. A series event close to
 * a detailed restart is the same restart, so it's dropped. Each says which server (`group`)
 * and what happened (`what`).
 */
export function chartEvents({ detailed = [], series = [], show = { restarts: true, jumps: true }, bucketSec = 60 }) {
  const near = Math.max(180, 2 * bucketSec) * 1000;
  const restarts = detailed.filter((e) => e.kind === "restart");
  const out = [];
  for (const e of detailed) {
    if ((e.kind === "jump" && show.jumps) || (e.kind === "restart" && show.restarts)) {
      out.push({ t: e.t, kind: e.kind, group: e.group ?? null, what: eventWords(e), select: e.t });
    }
  }
  if (show.restarts) {
    for (const e of series) {
      const ms = Date.parse(e.t);
      if (restarts.some((r) => Math.abs(Date.parse(r.t) - ms) <= near)) continue;
      out.push({ t: e.t, kind: e.kind, group: e.group ?? null, what: eventWords(e), select: null });
    }
  }
  return out;
}
